import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import AuthenticatedNavBar from '../../components/ui/AuthenticatedNavBar';
import ClaimInput from './components/ClaimInput';
import VerificationResults from './components/VerificationResults';
import LoadingSkeleton from './components/LoadingSkeleton';
import RecentVerifications from './components/RecentVerifications';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import UpgradeModal from '../../components/ui/UpgradeModal';

const FactChecking = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [recentVerifications, setRecentVerifications] = useState([]);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const API_URL = import.meta.env?.VITE_SERVER_URL || 'http://localhost:4000';

  // Load real user & recent verifications
  useEffect(() => {
    const load = async () => {
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          setUser(JSON.parse(stored));
        }
        const res = await fetch(`${API_URL}/api/user/data`, { credentials: 'include' });
        const data = await res.json();
        if (data?.success) {
          const merged = {
            ...JSON.parse(localStorage.getItem('user') || '{}'),
            name: data?.userData?.name,
            email: data?.userData?.email,
            isVerified: data?.userData?.isAccountVerified ?? false
          };
          setUser(merged);
          localStorage.setItem('user', JSON.stringify(merged));
        }
      } catch (_) {}

      const saved = localStorage.getItem('ddf_recent_verifications');
      if (saved) {
        try { setRecentVerifications(JSON.parse(saved)); } catch {}
      }
    };
    load();
  }, []);

  // Map new backend API -> UI shape (supports True/False/Maybe verdicts)
  const mapVerificationToUI = (api) => {
    return {
      // New verdict-based fields
      verdict: api?.verdict || 'MAYBE',
      confidence: api?.confidence || 5,
      
      // Legacy compatibility fields  
      credibilityScore: api?.credibilityScore || 50,
      summary: api?.summary || 'Verification completed.',
      keyFindings: api?.keyFindings || [],
      
      // Evidence sources
      evidence: (api?.evidence || []).map(s => ({
        title: s?.title || 'Source',
        description: s?.description || '',
        url: s?.url || s?.link,
        domain: s?.domain || (s?.url || s?.link || '').split('/')[2] || '',
        publishDate: s?.publishDate || '',
        reliability: s?.reliability || 80
      })),
      
      // Status for UI display
      status: api?.status || 'unverified'
    };
  };


  const handleVerification = async (claimData) => {
    setLoading(true);
    setResults(null);

    try {
      const res = await fetch(`${API_URL}/api/agents/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ article: claimData?.content })
      });
      const data = await res.json();
      
      // Debug: Log the API response
      console.log('Verification API Response:', data);
      
      // Handle usage limit exceeded
      if (res.status === 429 || data?.upgradeRequired) {
        setShowUpgradeModal(true);
        showToast('Usage limit reached', 'error');
        return;
      }
      
      if (!res.ok) {
        throw new Error(data?.message || 'Verification failed');
      }
      
      const ui = mapVerificationToUI(data);
      console.log('Mapped UI data:', ui);  // Debug: Log mapped data
      setResults(ui);

      // Add to recent verifications
      const newVerification = {
        id: Date.now(),
        originalClaim: claimData?.content,
        credibilityScore: ui?.credibilityScore,
        evidenceCount: ui?.evidence?.length,
        type: claimData?.type,
        timestamp: new Date()?.toISOString()?.split('T')?.[0],
        timeAgo: "Just now"
      };

      const updatedVerifications = [newVerification, ...recentVerifications?.slice(0, 9)];
      setRecentVerifications(updatedVerifications);
      localStorage.setItem('ddf_recent_verifications', JSON.stringify(updatedVerifications));

      showToast('Verification completed successfully');
    } catch (error) {
      console.error('Verification failed:', error);
      showToast('Verification failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (message, type = 'success') => {
    showToast(message, type);
  };

  const handleNewVerification = () => {
    setResults(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectVerification = (verification) => {
    // Mock loading previous verification results
    const mockPreviousResult = {
      ...mapVerificationToUI({ status: 'verified', analysis: 'Mock analysis for ' + verification?.originalClaim, sources: [{ title: 'Mock Source', link: 'https://example.com' }] }),
      originalClaim: verification?.originalClaim,
      credibilityScore: verification?.credibilityScore
    };
    setResults(mockPreviousResult);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const handleClearHistory = () => {
    setRecentVerifications([]);
    localStorage.removeItem('ddf_recent_verifications');
    showToast('Verification history cleared');
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      // ignore
    } finally {
      localStorage.removeItem('ddf_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(''), 3000);
  };

  return (
    <>
      <Helmet>
        <title>Fact Checking - DDF Agent</title>
        <meta name="description" content="AI-powered fact verification and claim analysis for journalists and content creators" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <AuthenticatedNavBar user={user} onLogout={handleLogout} />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} color="white" />
                </div>
                <div>
                  <h1 className="text-3xl font-heading font-bold text-foreground">
                    Fact Checking
                  </h1>
                  <p className="text-muted-foreground">
                    Verify claims and analyze content credibility with AI-powered tools
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <Icon name="Shield" size={16} color="var(--color-success)" />
                  <span>Secure verification</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Zap" size={16} color="var(--color-warning)" />
                  <span>Real-time analysis</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Database" size={16} color="var(--color-accent)" />
                  <span>Multiple source cross-reference</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Claim Input */}
                <ClaimInput onVerify={handleVerification} loading={loading} />

                {/* Results Section */}
                {loading && <LoadingSkeleton />}
                {results && !loading && (
                  <VerificationResults 
                    results={results} 
                    onCopy={handleCopy}
                    onNewVerification={handleNewVerification}
                  />
                )}

                {/* Quick Actions */}
                {!results && !loading && (
                  <div className="bg-card rounded-xl shadow-soft border border-border p-6">
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                      Quick Actions
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Button
                        variant="outline"
                        size="lg"
                        iconName="FileText"
                        iconPosition="left"
                        className="justify-start"
                        onClick={() => document.querySelector('textarea')?.focus()}
                      >
                        Verify Text Claim
                      </Button>
                      <Button
                        variant="outline"
                        size="lg"
                        iconName="Link"
                        iconPosition="left"
                        className="justify-start"
                        onClick={() => {
                          const textarea = document.querySelector('textarea');
                          textarea?.focus();
                          textarea.placeholder = "Paste URL to analyze...";
                        }}
                      >
                        Analyze URL
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Recent Verifications */}
                <RecentVerifications 
                  verifications={recentVerifications}
                  onSelectVerification={handleSelectVerification}
                  onClearHistory={handleClearHistory}
                />

                {/* Tips & Guidelines */}
                <div className="bg-card rounded-xl shadow-soft border border-border p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-accent rounded-lg flex items-center justify-center">
                      <Icon name="Lightbulb" size={20} color="white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading font-semibold text-foreground">
                        Verification Tips
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Best practices for fact-checking
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <div className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5" />
                      <span>Be specific with claims and include context</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5" />
                      <span>Check multiple sources for comprehensive verification</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5" />
                      <span>Review evidence sources and their reliability scores</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5" />
                      <span>Consider the publication date and relevance</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Toast Notification */}
        {toastMessage && (
          <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2 duration-300">
            <div className={`px-4 py-3 rounded-lg shadow-elevated border flex items-center space-x-3 ${
              toastType === 'error' ?'bg-error text-error-foreground border-error/20' :'bg-success text-success-foreground border-success/20'
            }`}>
              <Icon 
                name={toastType === 'error' ? 'AlertCircle' : 'CheckCircle'} 
                size={16} 
                color="currentColor" 
              />
              <span className="text-sm font-medium">{toastMessage}</span>
            </div>
          </div>
        )}
        
        {/* Upgrade Modal */}
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="fact checking"
          usageInfo={{
            usage: 10,
            limit: 10
          }}
        />
      </div>
    </>
  );
};

export default FactChecking;